import { LitElement, html, css, nothing } from 'lit';
import { money, count, mediumDate } from '../lib/format.js';

/**
 * The analog panel (Estimate Tool 6.6; Phase E5): the base price as a worked line, the
 * comparable range beneath it, and — collapsed — every project that contributed, with its
 * client, its two ratings, its gross, its age and its weight.
 *
 * It exists because of Section 7.6. The usable pool's gross runs from $200 to $500,102, and no
 * weighting of two client-difficulty axes can explain a fiftyfold spread between the quartiles.
 * So the number is never shown alone: the band says how much judgement is still the
 * estimator's, and the table says which projects produced it, because *an estimator who cannot
 * see which projects produced the number cannot judge whether to trust it.*
 *
 * **The lifted floor is not a footnote.** When too few analogs clear the similarity floor the
 * model lowers it rather than refusing, and this panel says so in words the reader cannot miss.
 * A model that silently widens its own tolerance is telling the estimator a lie about how
 * comparable its comparables are (7.2).
 *
 * The component computes nothing. Every number here arrives from `POST /estimate/price`; it
 * rounds for display and otherwise only arranges what it was given, so what the screen shows and
 * what a spreadsheet reproduces from the same payload cannot differ.
 */
class EstimateAnalogs extends LitElement {
  static properties = {
    price: { type: Object },
    // The two adjustments, when there are any: the panel then renders the whole worked line of
    // Estimate Tool 6.6 rather than the base alone, so the price is shown in one place.
    adjustments: { type: Object },
    loading: { type: Boolean },
    error: { type: String },
    open: { type: Boolean },
  };

  constructor() {
    super();
    this.price = null;
    this.adjustments = null;
    this.loading = false;
    this.error = null;
    this.open = false;
  }

  static styles = css`
    :host {
      display: block;
    }
    .worked {
      display: grid;
      gap: 2px;
      font-variant-numeric: tabular-nums;
      max-width: 52ch;
    }
    .line {
      display: flex;
      align-items: baseline;
      gap: 12px;
      padding: 3px 0;
    }
    .line .what {
      flex: 1;
      color: var(--ink);
    }
    .line .amount {
      text-align: right;
      min-width: 11ch;
      color: var(--ink-strong);
      font-weight: 600;
    }
    .line.base .what,
    .line.base .amount {
      font-size: 15px;
    }
    .line.adj .what {
      padding-left: 14px;
      color: var(--ink-muted);
    }
    .line.total {
      border-top: 2px solid var(--border);
      margin-top: 4px;
      padding-top: 8px;
    }
    .line.total .what,
    .line.total .amount {
      font-size: 18px;
      font-weight: 800;
      color: var(--ink-strong);
    }
    .band {
      margin-top: 10px;
      font-size: 13px;
      color: var(--ink-muted);
      max-width: 52ch;
    }
    .band strong {
      color: var(--ink);
      font-variant-numeric: tabular-nums;
    }
    /* The lifted floor: stated, not hinted. */
    .lifted {
      margin: 12px 0 0;
      padding: 8px 12px;
      border: 1px solid var(--status-caution, var(--border));
      border-left: 3px solid var(--status-caution, var(--ink-muted));
      border-radius: 6px;
      background: var(--surface-2);
      font-size: 13px;
      color: var(--ink);
      max-width: 62ch;
      line-height: 1.5;
    }
    details {
      margin-top: 14px;
      border-top: 1px solid var(--border-soft);
      padding-top: 10px;
    }
    summary {
      cursor: pointer;
      font-size: 13px;
      font-weight: 700;
      color: var(--ink-strong);
    }
    .scroll {
      overflow-x: auto;
      margin-top: 10px;
    }
    table {
      border-collapse: collapse;
      width: 100%;
      font-size: 12.5px;
    }
    th,
    td {
      text-align: left;
      padding: 5px 9px;
      border-bottom: 1px solid var(--border-soft);
      white-space: nowrap;
    }
    th {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--ink-muted);
      font-weight: 700;
    }
    td.num,
    th.num {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
    .mono {
      font-family: var(--font-eyebrow);
      font-size: 11px;
      color: var(--ink-muted);
    }
    /* The weight as a bar as well as a number: which analogs are carrying the price should be
       visible without reading a column of four-decimal figures. */
    .bar {
      display: inline-block;
      height: 7px;
      background: var(--accent);
      border-radius: 2px;
      min-width: 1px;
      vertical-align: middle;
      margin-right: 6px;
    }
    .state,
    .empty {
      color: var(--ink-muted);
      font-size: 13.5px;
      padding: 8px 0;
    }
    .error {
      color: var(--status-overloaded);
      font-weight: 600;
    }
  `;

  render() {
    if (this.error) return html`<p class="state error">${this.error}</p>`;
    if (this.loading && !this.price) return html`<p class="state">Pricing…</p>`;
    if (!this.price) return nothing;

    const p = this.price;
    if (p.base === null) {
      return html`
        <p class="state empty">
          No comparable project can price this yet.
          ${
            p.pool_size === 0
              ? html`Nothing in the history is usable — Data Hygiene says why.`
              : html`${count(p.pool_size)} projects are usable, but none could be scored.`
          }
        </p>
      `;
    }

    const max = Math.max(...p.analogs.map((a) => a.weight), 0);
    const adj = this.adjustments;
    // The price is a worked line, not a total (6.6): an estimator has to be able to see what
    // the base was before the adjustments moved it, and in which order they moved it.
    const tipped = adj && adj.scale_amount !== null && adj.scale_amount !== undefined;
    const worked = adj && (tipped || adj.value_add_pct > 0 || adj.principal_investment_pct > 0);

    return html`
      <div class="worked">
        <div class="line base">
          <span class="what"
            >Base price
            <span class="mono"
              >(${count(p.analog_count)} comparable
              ${p.analog_count === 1 ? 'project' : 'projects'})</span
            ></span
          >
          <span class="amount">${money(p.base)}</span>
        </div>
        ${
          tipped
            ? html`
                <div class="line adj">
                  <span class="what"
                    >Tip the scale
                    <span class="mono"
                      >${adj.scale_pct}% ·
                      ${adj.scale_pct < 100 ? 'toward the client' : 'toward BE'}</span
                    ></span
                  >
                  <span class="amount">${money(adj.scale_amount)}</span>
                </div>
              `
            : nothing
        }
        ${
          worked
            ? html`
                <div class="line adj">
                  <span class="what"
                    >Value Add <span class="mono">+${adj.value_add_pct}%</span></span
                  >
                  <span class="amount">${money(adj.value_add_amount)}</span>
                </div>
                <div class="line adj">
                  <span class="what"
                    >Principal Investment
                    <span class="mono">−${adj.principal_investment_pct}%</span></span
                  >
                  <span class="amount">${money(adj.principal_investment_amount)}</span>
                </div>
                <div class="line total">
                  <span class="what">Estimate</span>
                  <span class="amount">${money(adj.total)}</span>
                </div>
              `
            : nothing
        }
      </div>

      <p class="band">
        Comparable range (25th–75th percentile)
        <strong>${money(p.band_low)} – ${money(p.band_high)}</strong>.
        ${
          p.mean !== null && p.base !== null && Math.abs(p.mean - p.base) > p.base * 0.15
            ? html`The weighted mean is <strong>${money(p.mean)}</strong>, well away from the median
                — a few large projects are pulling it.`
            : nothing
        }
      </p>

      ${
        p.floor_lifted
          ? html`<p class="lifted" role="note">
              <strong>The similarity floor was lifted.</strong> Only ${count(p.above_floor)} past
              ${p.above_floor === 1 ? 'project was' : 'projects were'} close enough to this client
              at the usual threshold of ${p.settings_snapshot?.min_similarity ?? '—'}, and
              ${count(p.settings_snapshot?.min_count ?? 0)} are wanted, so the model reached down to
              ${p.effective_floor === null ? '—' : p.effective_floor.toFixed(2)} to find them. These
              comparables are less comparable than usual; treat the number as a starting point
              rather than an answer.
            </p>`
          : nothing
      }

      <details ?open=${this.open}>
        <summary>
          The ${count(p.analog_count)} ${p.analog_count === 1 ? 'project' : 'projects'} behind this
          number
        </summary>
        <div class="scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">Project</th>
                <th scope="col">Client</th>
                <th scope="col" class="num">Org</th>
                <th scope="col" class="num">Human</th>
                <th scope="col">Completed</th>
                <th scope="col" class="num">Age</th>
                <th scope="col" class="num">Billed</th>
                <th scope="col" class="num">Similarity</th>
                <th scope="col" class="num">Recency</th>
                <th scope="col" class="num">Weight</th>
              </tr>
            </thead>
            <tbody>
              ${p.analogs.map(
                (a) => html`
                  <tr>
                    <td>
                      <div class="mono">${a.number ?? '—'}</div>
                      ${a.name ?? ''}
                    </td>
                    <td>${a.client ?? '—'}</td>
                    <td class="num">${a.org_complexity}</td>
                    <td class="num">${a.human_factor}</td>
                    <td>${a.date ? mediumDate(a.date) : '—'}</td>
                    <td class="num">${Math.round(a.age_months)} mo</td>
                    <td class="num">${money(a.gross)}</td>
                    <td class="num">${a.similarity.toFixed(2)}</td>
                    <td class="num">${a.recency.toFixed(2)}</td>
                    <td class="num">
                      <span
                        class="bar"
                        style=${`width:${max > 0 ? Math.max(1, Math.round((a.weight / max) * 34)) : 1}px`}
                        aria-hidden="true"
                      ></span
                      >${(a.weight_fraction * 100).toFixed(1)}%
                    </td>
                  </tr>
                `,
              )}
            </tbody>
          </table>
        </div>
      </details>
    `;
  }
}

customElements.define('estimate-analogs', EstimateAnalogs);
